import type { AuthIntentPreview } from "@/context/auth/types"
import {
  defaultProjectTemplateStarterPanel,
  type ProjectStarterTemplateID,
  type StarterPanelItem,
} from "@/services/project-starters"

export const AUTH_STARTER_HANDOFF_PATH = "/starter-handoff"
const AUTH_STARTER_HANDOFF_KEY = "openanalyst_auth_starter_handoff"
const AUTH_STARTER_HANDOFF_TTL_MS = 30 * 60 * 1000

const starterTemplateAllowlist = {
  "ai-marketing-workspace": "ai-marketing-workspace",
  "email-revenue-system": "email-revenue-system",
  "ecommerce-ads-analysis": "ecommerce-ads-analysis",
  "google-ads-growth-review": "google-ads-growth-review",
  "facebook-ads-performance-review": "facebook-ads-performance-review",
  "paid-ads-creative-strategy": "paid-ads-creative-strategy",
  "funnel-conversion-analysis": "funnel-conversion-analysis",
  "lead-generation-system": "lead-generation-system",
  "seo-authority-growth-system": "seo-authority-growth-system",
  "youtube-social-growth-system": "youtube-social-growth-system",
  "analytics-measurement-readiness": "analytics-measurement-readiness",
  "openanalyst-shopify-manager": "openanalyst-shopify-manager",
  "job-research": "job-research",
} satisfies Record<ProjectStarterTemplateID, ProjectStarterTemplateID>

export type AuthStarterHandoff = {
  version: 1
  authIntentId: string
  starterKey: string
  templateId: ProjectStarterTemplateID
  title?: string | null
  subtitle?: string | null
  reason?: string | null
  ctaLabel?: string | null
  expiresAt: number
  createdDirectory?: string | null
  fallbackPath?: string | null
}

export type AuthStarterHandoffSkipReason =
  | "existing_local_projects"
  | "missing_handoff"
  | "starter_unavailable"
  | "user_skipped"

type AuthStarterHandoffEvent = "received" | "resolved" | "unknown_key" | "skipped" | "created" | "failed"

type AuthStarterHandoffDiagnosticFields = {
  starterKey?: string
  templateId?: ProjectStarterTemplateID
  skipReason?: AuthStarterHandoffSkipReason
  message?: string
}

function session() {
  if (typeof sessionStorage === "undefined") return
  return sessionStorage
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function readHandoff(raw: string | null, now: number): AuthStarterHandoff | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as unknown
    if (!isRecord(value)) return null
    if (value.version !== 1) return null
    if (typeof value.authIntentId !== "string" || !value.authIntentId) return null
    if (typeof value.starterKey !== "string" || !value.starterKey) return null
    if (typeof value.templateId !== "string") return null
    if (typeof value.expiresAt !== "number" || value.expiresAt <= now) return null
    const templateId = starterTemplateForIntentKey(value.starterKey)
    if (!templateId || templateId !== value.templateId) return null
    return {
      version: 1,
      authIntentId: value.authIntentId,
      starterKey: value.starterKey,
      templateId,
      title: typeof value.title === "string" ? value.title : null,
      subtitle: typeof value.subtitle === "string" ? value.subtitle : null,
      reason: typeof value.reason === "string" ? value.reason : null,
      ctaLabel: typeof value.ctaLabel === "string" ? value.ctaLabel : null,
      expiresAt: value.expiresAt,
      createdDirectory: typeof value.createdDirectory === "string" ? value.createdDirectory : null,
      fallbackPath: typeof value.fallbackPath === "string" ? value.fallbackPath : null,
    }
  } catch {
    return null
  }
}

function saveAuthStarterHandoff(handoff: AuthStarterHandoff) {
  session()?.setItem(AUTH_STARTER_HANDOFF_KEY, JSON.stringify(handoff))
}

export function diagnoseAuthStarterHandoff(event: AuthStarterHandoffEvent, fields: AuthStarterHandoffDiagnosticFields = {}) {
  console.info("[AuthStarterHandoff]", { event, ...fields })
}

export function starterTemplateForIntentKey(key?: string | null): ProjectStarterTemplateID | null {
  const normalized = key?.trim().toLowerCase()
  if (!normalized) return null
  return starterTemplateAllowlist[normalized as keyof typeof starterTemplateAllowlist] ?? null
}

export function starterPanelItemForTemplate(templateId: ProjectStarterTemplateID): StarterPanelItem | null {
  return defaultProjectTemplateStarterPanel()
    .sections.flatMap((section) => section.items)
    .find((item) => item.action.kind === "createProject" && item.action.templateId === templateId) ?? null
}

export function loadAuthStarterHandoff(now = Date.now()): AuthStarterHandoff | null {
  const handoff = readHandoff(session()?.getItem(AUTH_STARTER_HANDOFF_KEY) ?? null, now)
  if (handoff) return handoff
  clearAuthStarterHandoff()
  return null
}

export function resolveAuthStarterHandoffPath(fallbackPath: string) {
  const handoff = loadAuthStarterHandoff()
  if (!handoff) return fallbackPath
  saveAuthStarterHandoff({ ...handoff, fallbackPath })
  return AUTH_STARTER_HANDOFF_PATH
}

export function clearAuthStarterHandoff() {
  session()?.removeItem(AUTH_STARTER_HANDOFF_KEY)
}

export function receiveAuthStarterHandoff(preview: AuthIntentPreview, now = Date.now()): AuthStarterHandoff | null {
  const starterKey = preview.starterIntent?.key?.trim()
  if (!starterKey) {
    clearAuthStarterHandoff()
    return null
  }
  diagnoseAuthStarterHandoff("received", { starterKey })
  const templateId = starterTemplateForIntentKey(starterKey)
  if (!templateId) {
    diagnoseAuthStarterHandoff("unknown_key", { starterKey })
    clearAuthStarterHandoff()
    return null
  }
  const previewExpiresAt = preview.expiresAt ? Date.parse(preview.expiresAt) : NaN
  const handoff = {
    version: 1,
    authIntentId: preview.authIntentId,
    starterKey,
    templateId,
    title: preview.starterIntent?.title ?? null,
    subtitle: preview.starterIntent?.subtitle ?? null,
    reason: preview.starterIntent?.reason ?? null,
    ctaLabel: preview.starterIntent?.ctaLabel ?? null,
    expiresAt:
      Number.isFinite(previewExpiresAt) && previewExpiresAt > now
        ? Math.min(previewExpiresAt, now + AUTH_STARTER_HANDOFF_TTL_MS)
        : now + AUTH_STARTER_HANDOFF_TTL_MS,
  } satisfies AuthStarterHandoff
  saveAuthStarterHandoff(handoff)
  diagnoseAuthStarterHandoff("resolved", { starterKey, templateId })
  return handoff
}

// Self-contained starter handoff from a plain `?starter=<key>` link (e.g.
// https://app.openanalyst.com/login?starter=job-research). Unlike
// receiveAuthStarterHandoff this needs no 10x-minted authIntentId / preview — the
// embeddable production link carries the starter key directly. Validates the key
// against the same allowlist and writes the same sessionStorage handoff record the
// rest of the flow (resolveAuthStarterHandoffPath → /starter-handoff) consumes.
export function receiveStarterHandoffFromKey(key: string | null | undefined, now = Date.now()): AuthStarterHandoff | null {
  const starterKey = key?.trim()
  if (!starterKey) return null
  diagnoseAuthStarterHandoff("received", { starterKey })
  const templateId = starterTemplateForIntentKey(starterKey)
  if (!templateId) {
    diagnoseAuthStarterHandoff("unknown_key", { starterKey })
    return null
  }
  const panelItem = starterPanelItemForTemplate(templateId)
  const handoff = {
    version: 1,
    authIntentId: `starter:${templateId}`,
    starterKey,
    templateId,
    title: panelItem?.title ?? null,
    subtitle: panelItem?.description ?? null,
    reason: null,
    ctaLabel: panelItem?.actionLabel ?? null,
    expiresAt: now + AUTH_STARTER_HANDOFF_TTL_MS,
  } satisfies AuthStarterHandoff
  saveAuthStarterHandoff(handoff)
  diagnoseAuthStarterHandoff("resolved", { starterKey, templateId })
  return handoff
}

export function markAuthStarterHandoffCreated(createdDirectory: string): AuthStarterHandoff | null {
  const handoff = loadAuthStarterHandoff()
  if (!handoff) return null
  const next = { ...handoff, createdDirectory } satisfies AuthStarterHandoff
  saveAuthStarterHandoff(next)
  return next
}
